import { DirectionalLight, Matrix4, type OrthographicCamera, Vector3 } from 'three'
import { ToonLighting } from './toonMaterial'

/**
 * The sun's shadow map — `Gameplay.unity`'s one `Directional Light`, as far as
 * the export lets it be read.
 *
 * 🔍 Read off the light itself (`Gameplay.unity`:2647-2662):
 *   `m_Shadows.m_Type: 2` (Soft Shadows), `m_Strength: 0.382`, `m_Bias: 0.05`,
 *   `m_NormalBias: 0.4`, `m_NearPlane: 0.2`; `m_CullingMask.m_Bits: 4294967295`
 *   — every layer casts, the `Invisible` layer a bus in a tunnel stub is moved
 *   to included.
 *
 * ❓ Not readable, and declared as such in `GAMEPLAY-CHECKLIST.md` §10:
 *   - **Shadow distance, cascades, map resolution, soft-shadow quality.** All
 *     live on the URP asset, and `URP.asset` exports as a bare header (the
 *     `MonoBehaviour` was stripped to a name). `QualitySettings.asset`'s
 *     `shadowDistance: 30` is the **built-in** pipeline's field and the project
 *     is on URP (`customRenderPipeline` points at that same stripped asset), so
 *     it says nothing about what shipped.
 *   - **Bias.** The light carries a stripped `UniversalAdditionalLightData`
 *     (`Gameplay.unity`:4254, guid `3ace53bb…`), whose `usePipelineSettings`
 *     decides whether the light's own 0.05 / 0.4 or the URP asset's are used —
 *     and either way URP scales them by shadow-map texel size, which depends on
 *     the unreadable resolution. three measures `normalBias` in world units and
 *     `bias` in depth units, so the numbers are not transferable as they stand.
 *
 * What the port does with that:
 *   - The strength is exact. three's `getShadow` returns
 *     `mix(1, occlusion, intensity)`, which is URP's
 *     `LerpWhiteTo(attenuation, _MainLightShadowParams.x)` with `x` = the
 *     light's `shadowStrength`. `shadow.intensity = 0.382`.
 *   - The frustum is fitted to what the camera can see between the level's
 *     lowest and highest surface (`fitSunShadow`), which is the one-cascade
 *     directional fit with the distance question removed: the gameplay camera
 *     is orthographic and never moves within a level, so "everything on screen"
 *     is a fixed box. A frustum fitted to the level's whole bounding box would
 *     spend most of the map on the 108×162 ground plate off-screen.
 *   - `MAP_SIZE` and `NORMAL_BIAS_TEXELS` are ❓ PLACEHOLDER: one texel of
 *     normal offset, which is what keeps a surface facing the light from
 *     shadowing itself, and no depth bias.
 */
export const SUN_SHADOW = {
  /** 🔍 `m_Shadows.m_Strength`. */
  strength: 0.382,
  /** ❓ PLACEHOLDER — the URP asset's `m_MainLightShadowmapResolution` is stripped. */
  mapSize: 2048,
  /**
   * ❓ PLACEHOLDER — normal offset, in shadow-map texels of the fitted frustum.
   * The light's `m_NormalBias: 0.4` is in the same unit *before* URP's soft
   * kernel scaling, whose factor depends on the stripped quality setting.
   */
  normalBiasTexels: 1,
  /** How far up the sun's ray the light object sits from the fitted centre. Any positive value; orthographic. */
  distance: 100,
  /** Padding around the fitted box, world units, so a caster on the very edge still lands in the map. */
  margin: 1,
} as const

/**
 * The scene's sun, casting.
 *
 * Direction and colour are the scene's `Directional Light`; the intensity is
 * three's, for the Lambert placeholders — every shipped surface goes through
 * `ToonMaterial`, which reads `ToonLighting.direction` itself and only takes
 * the *shadow* from this light. The two directions are the same object, so
 * they cannot drift apart.
 */
export function createSun(): DirectionalLight {
  const sun = new DirectionalLight(0xf4f4f4, 1.9)
  sun.position.copy(ToonLighting.direction.value).multiplyScalar(-SUN_SHADOW.distance)
  sun.castShadow = true
  sun.shadow.intensity = SUN_SHADOW.strength
  sun.shadow.mapSize.set(SUN_SHADOW.mapSize, SUN_SHADOW.mapSize)
  sun.shadow.bias = 0
  return sun
}

const UP = new Vector3(0, 1, 0)
const _forward = new Vector3()
const _point = new Vector3()
const _centre = new Vector3()
const _rotation = new Matrix4()
const _corners: Vector3[] = Array.from({ length: 8 }, () => new Vector3())

/**
 * Fit the sun's orthographic shadow camera to what `camera` can see between
 * the heights `yMin` and `yMax`.
 *
 * The four corner rays of the orthographic view are cut by the two horizontal
 * planes, which gives the eight corners of the visible slab; those are taken
 * into the light's frame and the shadow camera's box is their extent plus
 * `margin`. The light is re-seated on the sun's ray through the slab's centre
 * so the box is centred on the map. Call after the camera has its level pose
 * and its size, and again whenever either changes.
 *
 * `yMin`/`yMax` are the level's own surfaces — the elevator floor at −8 and
 * the tallest roof on the level, measured off the built scene — so a level
 * with no lift does not waste half the map on a slab nothing occupies.
 */
export function fitSunShadow(
  sun: DirectionalLight,
  camera: OrthographicCamera,
  yMin: number,
  yMax: number,
  margin: number = SUN_SHADOW.margin,
): void {
  camera.updateMatrixWorld()
  _forward.set(0, 0, -1).applyQuaternion(camera.quaternion).normalize()

  let i = 0
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      // The near-plane point of this corner's ray.
      _point.set(sx, sy, -1).unproject(camera)
      for (const y of [yMin, yMax]) {
        const corner = _corners[i++]!
        if (Math.abs(_forward.y) < 1e-6) {
          // A level camera never looks along the horizon, but a ray that did
          // would never meet the planes; fall back to the clip planes.
          corner.set(sx, sy, y === yMin ? -1 : 1).unproject(camera)
        } else {
          const t = (y - _point.y) / _forward.y
          corner.copy(_point).addScaledVector(_forward, t)
        }
      }
    }
  }

  _centre.set(0, 0, 0)
  for (const c of _corners) _centre.add(c)
  _centre.multiplyScalar(1 / _corners.length)

  const direction = ToonLighting.direction.value
  sun.target.position.copy(_centre)
  sun.position.copy(_centre).addScaledVector(direction, -SUN_SHADOW.distance)
  sun.updateMatrixWorld()
  sun.target.updateMatrixWorld()

  // World → light: the same basis `DirectionalLightShadow.updateMatrices`
  // builds (`lookAt(target)` with the default up), transposed.
  _rotation.lookAt(sun.position, _centre, UP).transpose()

  let minX = Infinity, maxX = -Infinity
  let minY = Infinity, maxY = -Infinity
  let minZ = Infinity, maxZ = -Infinity
  for (const c of _corners) {
    _point.copy(c).sub(sun.position).applyMatrix4(_rotation)
    if (_point.x < minX) minX = _point.x
    if (_point.x > maxX) maxX = _point.x
    if (_point.y < minY) minY = _point.y
    if (_point.y > maxY) maxY = _point.y
    if (_point.z < minZ) minZ = _point.z
    if (_point.z > maxZ) maxZ = _point.z
  }

  const box = sun.shadow.camera
  box.left = minX - margin
  box.right = maxX + margin
  box.bottom = minY - margin
  box.top = maxY + margin
  // The camera looks down −Z: the nearest corner has the largest z.
  box.near = -maxZ - margin
  box.far = -minZ + margin
  box.updateProjectionMatrix()

  const texel = Math.max(box.right - box.left, box.top - box.bottom) / SUN_SHADOW.mapSize
  sun.shadow.normalBias = texel * SUN_SHADOW.normalBiasTexels
}
